/** 计算 Fabric 控件围绕中心旋转后的轴对齐包围框。 */
export function getRotatedTypographyBounds({
  left,
  top,
  width = 0,
  height = 0,
  scaleX = 1,
  scaleY = 1,
  angle = 0,
}) {
  const scaledWidth = Math.abs(width * scaleX)
  const scaledHeight = Math.abs(height * scaleY)
  const radians = (Number(angle) * Math.PI) / 180
  const boundsWidth =
    Math.abs(scaledWidth * Math.cos(radians)) +
    Math.abs(scaledHeight * Math.sin(radians))
  const boundsHeight =
    Math.abs(scaledWidth * Math.sin(radians)) +
    Math.abs(scaledHeight * Math.cos(radians))
  const centerX = left + scaledWidth / 2
  const centerY = top + scaledHeight / 2

  return {
    left: centerX - boundsWidth / 2,
    top: centerY - boundsHeight / 2,
    width: boundsWidth,
    height: boundsHeight,
  }
}

/** 根据模板保存数据还原编辑页排版控件旋转后的可视范围。 */
export function getTypographyEditorBounds(info, rectLeft, rectTop) {
  return getRotatedTypographyBounds({
    left: info.left - rectLeft,
    top: info.top - rectTop,
    width: info.width,
    height: info.height,
    scaleX: info.scaleX,
    scaleY: info.scaleY,
    angle: info.rotateDeg ?? (info.plankRotate ? -90 : 0),
  })
}

/** 将渲染后的排版图对齐到编辑器控件左上角。 */
export function alignTypographyToTemplateOrigin(typography, region) {
  const bounds = typography.getBoundingRect()
  typography.set({
    left: typography.left + region.left - bounds.left,
    top: typography.top + region.top - bounds.top,
  })
  typography.setCoords()
}

/** 在不缩放的情况下，将排版图平移回画布范围内。 */
export function constrainTypographyToCanvas(typography, canvas) {
  const bounds = typography.getBoundingRect()
  const offsetX =
    bounds.left < 0
      ? -bounds.left
      : bounds.left + bounds.width > canvas.width
      ? canvas.width - (bounds.left + bounds.width)
      : 0
  const offsetY =
    bounds.top < 0
      ? -bounds.top
      : bounds.top + bounds.height > canvas.height
      ? canvas.height - (bounds.top + bounds.height)
      : 0
  if (!offsetX && !offsetY) return

  typography.set({
    left: typography.left + offsetX,
    top: typography.top + offsetY,
  })
  typography.setCoords()
}

/**
 * 在排版图整体缩放后，补偿封边文字缩放，保证最终有效缩放不低于指定下限。
 */
export function preserveTypographyTextScale(
  textObjects,
  typographyScale,
  minimumScale = 0.5
) {
  if (typographyScale >= minimumScale) return

  const compensation = minimumScale / typographyScale
  textObjects.forEach((text) => {
    text.set({
      scaleX: (text.scaleX ?? 1) * compensation,
      scaleY: (text.scaleY ?? 1) * compensation,
    })
  })
}

/**
 * 按对象各边界的可见空间等比缩小，最低保留一半尺寸。
 * 缩放后仍越界的部分由后续平移处理。
 */
export function fitTypographyBounds(bounds, canvas) {
  const availableWidth = Math.min(
    canvas.width,
    bounds.left < 0 ? bounds.width + bounds.left : canvas.width - bounds.left
  )
  const availableHeight = Math.min(
    canvas.height,
    bounds.top < 0 ? bounds.height + bounds.top : canvas.height - bounds.top
  )
  const widthScale = bounds.width > 0 ? availableWidth / bounds.width : 1
  const heightScale = bounds.height > 0 ? availableHeight / bounds.height : 1

  return {
    scale: Math.max(0.5, Math.min(1, widthScale, heightScale)),
  }
}
